import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const navItems = [
    { to: "/a-propos", label: "À propos" },
    { to: "/tarifs", label: "Tarifs" },
    { to: "/actualites", label: "Actualités" },
    { to: "/contact", label: "Contact" },
];

/**
 * Site header.
 *   - variant = "default" → full nav + "Simuler mes économies" CTA (gold)
 *   - variant = "simulator" → stripped-down (logo + Contact link only), used on /simulateur
 */
export default function Header({ variant = "default" }) {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    // Close the mobile panel whenever the route changes
    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            data-testid="site-header"
            className="relative bg-white border-b border-border-default text-navy"
        >
            <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-4 px-4 md:px-6 lg:px-12 min-h-[64px]">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    aria-label="FOD by DMB Partners, accueil"
                    className="flex items-center py-3"
                >
                    <span className="hidden md:inline-flex">
                        <Logo variant="dark" fodWidth={88} />
                    </span>
                    <span className="inline-flex md:hidden">
                        <Logo variant="dark" fodWidth={70} />
                    </span>
                </Link>

                {variant === "default" ? (
                    <>
                        {/* Desktop nav */}
                        <nav
                            aria-label="Navigation principale"
                            className="hidden md:flex items-center gap-8 text-[15px] font-medium"
                        >
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                    className={({ isActive }) =>
                                        `inline-flex items-center py-2 border-b-[3px] transition-colors ${
                                            isActive
                                                ? "border-gold text-navy"
                                                : "border-transparent text-navy hover:text-gold-text"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/simulateur"
                                data-testid="nav-cta-button"
                                className="inline-flex items-center bg-gold text-navy font-semibold text-[15px] h-[46px] px-5 rounded-md hover:-translate-y-[1px] hover:shadow-md transition-all"
                            >
                                Simuler mes économies
                            </Link>
                        </nav>

                        {/* Mobile cluster */}
                        <div className="md:hidden flex items-center gap-2">
                            <Link
                                to="/simulateur"
                                data-testid="mobile-cta-button"
                                className="inline-flex items-center bg-navy text-white font-semibold text-sm h-11 px-3.5 rounded-md"
                            >
                                Simuler
                            </Link>
                            <button
                                data-testid="mobile-menu-button"
                                type="button"
                                aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                                aria-expanded={open}
                                aria-controls="site-mobile-menu"
                                onClick={() => setOpen((v) => !v)}
                                className="w-11 h-11 border border-border-default rounded-md bg-white text-navy inline-flex items-center justify-center"
                            >
                                {open ? <X size={22} strokeWidth={2} /> : <Menu size={22} strokeWidth={2} />}
                            </button>
                        </div>
                    </>
                ) : (
                    // Simulator variant: minimal header
                    <Link
                        to="/contact"
                        data-testid="nav-simulator-contact"
                        className="text-[15px] font-medium text-navy hover:text-gold-text inline-flex items-center min-h-[44px] px-2 -mr-2"
                    >
                        Contact
                    </Link>
                )}
            </div>

            {/* Mobile menu panel */}
            {variant === "default" && open && (
                <nav
                    id="site-mobile-menu"
                    aria-label="Menu mobile"
                    data-testid="mobile-menu-panel"
                    className="md:hidden border-t border-border-default bg-white px-4 pt-2 pb-4 flex flex-col"
                >
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-navy text-[17px] font-medium py-3.5 border-b border-border-soft last:border-b-0"
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
            )}
        </header>
    );
}
